import { MultiSelectOption } from './CommonTypes';
import * as yup from 'yup';
import { AttributeForm } from './resource';
import { isEmpty, isNumber } from 'lodash';

export enum TaskCreate_FormSections {
  INITIAL = 'INITIAL',
  AVAILABLE_SLOTS = 'AVAILABLE_SLOTS',
  QUALIFIED_RESOURCES = 'QUALIFIED_RESOURCES',
  INGRESS = 'INGRESS',
  REVIEW = 'REVIEW',
}

export enum FNOL_TaskTypes {
  NON_COMPLEX = 'NON_COMPLEX',
  COMPLEX = 'COMPLEX',
  CLAIM_OWNER = 'CLAIM_OWNER',
}

export const FNOL_TaskTypeOptions: MultiSelectOption[] = [
  {
    title: 'Non-Complex',
    value: FNOL_TaskTypes.NON_COMPLEX,
  },
  {
    title: 'Complex',
    value: FNOL_TaskTypes.COMPLEX,
  },
  {
    title: 'Claim Owner',
    value: FNOL_TaskTypes.CLAIM_OWNER,
  },
];

export interface FNOL_IsForcedForm {
  isForced: boolean;
}

export const isForcedDefaultValues = {
  isForced: false,
};

export const isForcedSchema = yup.object({
  isForced: yup.boolean().nullable(),
  resourceIds: yup.array().nullable(),
});

export interface FNOL_RequestForm {
  lossLocation: LossLocation;
  methodOfInspection?: string;
  catCode?: string;
  severity: Severity;
  peril?: string;
  startTime: Date;
  endTime: Date;
  dateOfLoss?: Date;
  durationInMinutes?: number;
  preferredLanguage?: string;
  assignmentCategory: AssignmentCategory;
  onSunday?: boolean;
  onMonday?: boolean;
  onTuesday?: boolean;
  onWednesday?: boolean;
  onThursday?: boolean;
  onFriday?: boolean;
  onSaturday?: boolean;
  fnolTaskType: FNOL_TaskTypes;
  claimAttributes: AttributeForm[];
  resourceIds: MultiSelectOption[];
  timeZone: string;
}

export const FNOL_RequestFormSchema = yup.object({
  lossLocation: yup.object({
    streetLine1: yup.string().nullable(),
    streetLine2: yup.string().nullable(),
    city: yup.string().required(),
    state: yup.string().required(),
    zip: yup.string().nullable(),
  }),
  methodOfInspection: yup
    .string()
    .nullable()
    .when('fnolTaskType', {
      is: (val: FNOL_TaskTypes) => val !== FNOL_TaskTypes.COMPLEX,
      then: yup.string().required(),
    }),
  catCode: yup.string().nullable(),
  severity: yup.string().nullable(),
  peril: yup.string().required(),
  startTime: yup
    .date()
    .nullable()
    .when('fnolTaskType', {
      is: (val: FNOL_TaskTypes) => val === FNOL_TaskTypes.NON_COMPLEX,
      then: yup.date().required(),
    }),
  endTime: yup
    .date()
    .test(
      'min_end_date',
      'End date time must be the same or after start date time',
      (value, context) => {
        const { startTime, fnolTaskType } = context.parent;
        if (fnolTaskType !== FNOL_TaskTypes.NON_COMPLEX) {
          return true;
        }

        if (!startTime || !value) {
          return false;
        }

        return value >= startTime;
      }
    )
    .nullable()
    .when('fnolTaskType', {
      is: (val: FNOL_TaskTypes) => val === FNOL_TaskTypes.NON_COMPLEX,
      then: yup.date().required(),
    }),
  durationInMinutes: yup
    .number()
    .transform((_, val) => {
      if (isNumber(val)) {
        return val;
      }

      if (isEmpty(val)) {
        return null;
      }

      return Number(val);
    })
    .nullable()
    .min(0)
    .max(360)
    .when('fnolTaskType', {
      is: (val: FNOL_TaskTypes) => val === FNOL_TaskTypes.NON_COMPLEX,
      then: yup
        .number()
        .transform((_, val) => {
          if (isNumber(val)) {
            return val;
          }

          if (isEmpty(val)) {
            return null;
          }

          return Number(val);
        })
        .nullable()
        .required()
        .min(0)
        .max(360),
    }),
  preferredLanguage: yup.string().nullable(),
  assignmentCategory: yup.string().required(),
  onSunday: yup.boolean().nullable(),
  onMonday: yup.boolean().nullable(),
  onTuesday: yup.boolean().nullable(),
  onWednesday: yup.boolean().nullable(),
  onThursday: yup.boolean().nullable(),
  onFriday: yup.boolean().nullable(),
  onSaturday: yup.boolean().nullable(),
  requiredAttributes: yup.object().nullable(),
  resourceIds: yup.array().nullable(),
  fnolTaskType: yup.string().required(),
  timeZone: yup
    .string()
    .nullable()
    .when('fnolTaskType', {
      is: (val: FNOL_TaskTypes) => val === FNOL_TaskTypes.NON_COMPLEX,
      then: yup.string().required(),
    }),
});

export interface FNOL_Request {
  lossLocation: LossLocation;
  methodOfInspection?: string;
  catCode?: string;
  severity?: Severity;
  peril?: string;
  startTime: string;
  endTime: string;
  durationInMinutes?: number;
  preferredLanguage?: string;
  assignmentCategory: AssignmentCategory;
  onSunday?: boolean;
  onMonday?: boolean;
  onTuesday?: boolean;
  onWednesday?: boolean;
  onThursday?: boolean;
  onFriday?: boolean;
  onSaturday?: boolean;
  requiredAttributes?: {
    [key: string]: string[];
  };
  resourceIds?: string[];
}

export enum AssignmentCategory {
  INITIAL = 'initial',
  SUPPLEMENT = 'supplement',
}

export const AssignmentCategoryOptions: MultiSelectOption[] = [
  { title: 'Initial', value: AssignmentCategory.INITIAL },
  { title: 'Supplement', value: AssignmentCategory.SUPPLEMENT },
];

export enum Severity {
  ZERO = '0',
  ONE = '1',
  TWO = '2',
}

export const SeverityOptions: MultiSelectOption[] = [
  { title: '0', value: Severity.ZERO },
  { title: '1', value: Severity.ONE },
  { title: '2', value: Severity.TWO },
];

export interface LossLocation {
  streetLine1: string;
  streetLine2?: string;
  city: string;
  state: string;
  zip: string;
}

export interface FNOL_AvailabilityResponse {
  availableResourcesSlots: AvailableSlots[];
  availableSlots: AvailableSlots[];
}

export interface AvailableSlots {
  projectedWorkStartTime: string;
  projectedWorkEndTime: string;
  resourceId?: string;
  resourceName?: string;
}

export interface FNOL_ClaimOwnerOrComplexAvailabilityResponse {
  projectedWorkStartTime: string;
  methodOfInspection: string;
  resource?: FNOL_ResourceResponse;
}

export interface FNOL_ResourceResponse {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  teamId: string;
  workTeam?: boolean;
}

export const FNOL_RequestFormDefaultValues = {
  lossLocation: {
    streetLine1: '',
    streetLine2: '',
    city: '',
    state: '',
    zip: '',
  },
  startTime: new Date(),
  endTime: new Date(),
  durationInMinutes: 90,
  assignmentCategory: AssignmentCategory.INITIAL,
};
